import React from 'react';
import { KPIMetric } from '../../types/common';

interface DashboardCardProps extends KPIMetric {
  tooltip?: string;
  onClick?: () => void;
  loading?: boolean;
  trend?: { value: number; label?: string };
  icon?: React.ReactNode;
}

const accentBorderMap: Record<NonNullable<KPIMetric['accentColor']>, string> = {
  emerald: 'border-t-emerald-500',
  cyan:    'border-t-cyan-500',
  blue:    'border-t-blue-500',
  violet:  'border-t-violet-500',
  amber:   'border-t-amber-500',
  slate:   'border-t-slate-400',
};

const accentTextMap: Record<NonNullable<KPIMetric['accentColor']>, string> = {
  emerald: 'text-emerald-600',
  cyan:    'text-cyan-600',
  blue:    'text-blue-600',
  violet:  'text-violet-600',
  amber:   'text-amber-600',
  slate:   'text-slate-500',
};

export const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  value,
  subtext,
  accentColor = 'slate',
  tooltip,
  onClick,
  loading = false,
  trend,
  icon,
}) => {
  return (
    <div
      className={`
        bg-white rounded-xl border border-slate-200
        border-t-2 ${accentBorderMap[accentColor]}
        p-5 shadow-sm
        ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''}
        transition-all duration-150
      `}
      onClick={onClick}
      title={tooltip}
    >
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </p>
        {icon && (
          <span className={`${accentTextMap[accentColor]} opacity-70`}>{icon}</span>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          <div className="h-7 bg-slate-100 rounded animate-pulse w-3/4" />
          <div className="h-3 bg-slate-100 rounded animate-pulse w-1/2" />
        </div>
      ) : (
        <>
          <p className="text-2xl font-bold text-slate-900 leading-none mb-2">
            {value}
          </p>
          <div className="flex items-center gap-2">
            {trend && (
              <span className={`text-xs font-semibold ${trend.value >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value).toFixed(1)}%
              </span>
            )}
            {subtext && (
              <p className="text-xs text-slate-600">{subtext}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardCard;
